import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { productService } from "../services/productService";
import { EyeIcon, TrashIcon } from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import ProductDetailModal from "./ProductDetailModal";
import toast from "react-hot-toast";
import { baseURL } from '../../../lib/axios';


const ProductList = () => {
  const [page, setPage] = useState(1);
  const [dateFilter, setDateFilter] = useState("today");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["products", page, dateFilter],
    queryFn: () => productService.getProducts({ page, dateFilter }),
  });

  const deleteProductMutation = useMutation({
    mutationFn: (id) => productService.deleteProduct(id),
    onSuccess: () => {
     
      toast.success("Supression du produit reussi")
      
      refetch();
      setShowDeleteModal(false);
      setSelectedProduct(null); // Reset aussi le produit sélectionné
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression:", error);
      // Vous pouvez ajouter une notification d'erreur ici
      alert("Erreur lors de la suppression du produit");
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="text-red-500">Erreur lors du chargement des produits</div>
    );

  const products = data?.data || [];
  const pagination = data?.meta || {};

  const handleDeleteProduct = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedProduct?.id) {
      deleteProductMutation.mutate(selectedProduct.id);
    } else {
      console.error("Aucun produit sélectionné ou ID manquant");
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedProduct(null);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
 
  return (
    <div className="bg-white shadow-md min-h-full rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          Tous les articles
        </h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-2 mb-3 pt-4" aria-label="Tabs">
          {[
            { label: "Aujourd'hui", value: "today" },
            { label: "Ce mois", value: "month" },
            { label: "Tous", value: "all" },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setDateFilter(tab.value)}
              className={`px-4 py-2 text-sm font-medium transition cursor-pointer ${
                dateFilter === tab.value
                  ? "bg-primary text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Image
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Nom du produit
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Boutique
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Prix
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Date de publication
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">
                Actions
              </th>
            </tr>
          </thead>

          {products.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id}>
                  <td className="px-4 py-3">
                    <div className="relative flex">
                      {product.images && JSON.parse(product.images).slice(0, 3).map((image, index) => (
                        <div
                          key={index}
                          className={`absolute ${index > 0 ? `-${index * 2}` : '0'} left-0 top-0`}
                          style={{ zIndex: 3 - index }}
                        >
                          <img
                            src={`${baseURL}/storage/${image}`}
                            alt={`Image ${index + 1}`}
                            className="h-12 w-12 rounded object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {product.name}
                  </td>
                  <td className="px-4 py-3 text-sm">{product.shop?.name}</td>
                  <td className="px-4 py-3 text-sm">
                    {formatPrice(product.price)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {formatDate(product.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        title="Voir les détails"
                        className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                        onClick={() => setSelectedProduct(product)}
                      >
                        <EyeIcon className="h-5 w-5 text-blue-600" />
                      </button>
                      <button
                        title="Supprimer le produit"
                        className="p-2 hover:bg-red-100 rounded cursor-pointer"
                        onClick={() => handleDeleteProduct(product)}
                        disabled={deleteProductMutation.isPending}
                      >
                        <TrashIcon className={`h-5 w-5 ${
                          deleteProductMutation.isPending ? 'text-gray-400' : 'text-red-500'
                        }`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="6">
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucun produit n'a été publié aujourd'hui.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      {selectedProduct && !showDeleteModal && (
        <ProductDetailModal 
          product={selectedProduct} 
          onClose={() => setSelectedProduct(null)} 
        />
      )}

      {/* Modal de confirmation de suppression */}
      <DeleteConfirmation
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le produit"
        message={`Êtes-vous sûr de vouloir supprimer le produit "${selectedProduct?.name}" ?`}
        confirmButtonText={deleteProductMutation.isPending ? "Suppression..." : "Supprimer"}
        confirmButtonColor="red"
        isLoading={deleteProductMutation.isPending}
      />
    </div>
  );
};

export default ProductList;